import { resolveStorefrontBlockContract } from './blockContract'
import { frontendDiagnosticsEvents } from '@emporion/ui'
import {
  STOREFRONT_RUNTIME_HTML_KEY,
  buildStorefrontRuntimeCacheKey,
  resolveStorefrontRuntimePayload,
  type StorefrontRuntimeContext,
} from './storefront-runtime-contract'
import { fetchStorefrontRuntimeHtml } from './storefront-runtime-client'
import { logStorefrontRuntimeEvent, runtimeFallback } from './storefront-runtime-diagnostics'
import type { BlockInstance, CmsPage } from './types'

const enrichBlockWithRuntimeHtml = async ({
  block,
  context,
}: {
  block: BlockInstance
  context: StorefrontRuntimeContext
}): Promise<BlockInstance> => {
  const children = Array.isArray(block.children)
    ? await Promise.all(block.children.map((child) => enrichBlockWithRuntimeHtml({ block: child, context })))
    : undefined
  const nextBlock = children ? { ...block, children } : block

  const contract = resolveStorefrontBlockContract(nextBlock)
  if (contract.runtimeMode === 'storefront-runtime' && !contract.pluginId) {
    logStorefrontRuntimeEvent({
      level: 'warn',
      event: frontendDiagnosticsEvents.storefrontRuntime.invalidPayload,
      message: 'Storefront plugin runtime payload rejected before request',
      context: {
        pluginId: null,
        blockType: nextBlock.type,
        reason: 'missing_plugin_id',
      },
    })
    return runtimeFallback({
      block: nextBlock,
      pluginId: 'unknown-plugin',
      reason: 'missing_plugin_id',
    })
  }

  if (!contract.pluginId || contract.runtimeMode !== 'storefront-runtime') {
    return nextBlock
  }

  const preflight = resolveStorefrontRuntimePayload(nextBlock, context)
  if (!preflight.ok) {
    logStorefrontRuntimeEvent({
      level: 'warn',
      event: frontendDiagnosticsEvents.storefrontRuntime.invalidPayload,
      message: 'Storefront plugin runtime payload rejected before request',
      context: {
        pluginId: contract.pluginId,
        blockType: nextBlock.type,
        reason: preflight.reason,
      },
    })
    return runtimeFallback({
      block: nextBlock,
      pluginId: contract.pluginId,
      reason: preflight.reason,
    })
  }

  const cacheKey = buildStorefrontRuntimeCacheKey(preflight.payload)
  const rendered = await fetchStorefrontRuntimeHtml({ payload: preflight.payload, cacheKey })
  if (rendered.ok) {
    logStorefrontRuntimeEvent({
      level: 'info',
      event: frontendDiagnosticsEvents.storefrontRuntime.rendered,
      message: 'Storefront plugin runtime block rendered',
      context: {
        pluginId: preflight.payload.pluginId,
        blockType: preflight.payload.blockType,
        runtimeMode: preflight.payload.runtimeMode,
      },
    })
    return {
      ...nextBlock,
      props: {
        ...preflight.payload.props,
        [STOREFRONT_RUNTIME_HTML_KEY]: rendered.html,
      },
    }
  }

  const fetchKind =
    rendered.reason.toLowerCase().includes('timeout') || rendered.reason.toLowerCase().includes('timed out')
      ? 'timeout'
      : 'failed'
  logStorefrontRuntimeEvent({
    level: fetchKind === 'timeout' ? 'warn' : 'error',
    event:
      fetchKind === 'timeout'
        ? frontendDiagnosticsEvents.storefrontRuntime.timeout
        : frontendDiagnosticsEvents.storefrontRuntime.failed,
    message:
      fetchKind === 'timeout'
        ? 'Storefront plugin runtime request timed out'
        : 'Storefront plugin runtime request failed',
    context: {
      pluginId: preflight.payload.pluginId,
      blockType: preflight.payload.blockType,
      runtimeMode: preflight.payload.runtimeMode,
      reason: rendered.reason,
      cacheKey,
    },
  })
  return runtimeFallback({
    block: nextBlock,
    pluginId: preflight.payload.pluginId,
    reason: rendered.reason,
  })
}

export const enrichStorefrontPageRuntimeBlocks = async ({
  page,
  context,
}: {
  page: CmsPage
  context: StorefrontRuntimeContext
}): Promise<CmsPage> => {
  if (!Array.isArray(page.content) || page.content.length === 0) return page
  const content = await Promise.all(
    page.content.map((block) =>
      enrichBlockWithRuntimeHtml({
        block,
        context,
      }),
    ),
  )
  return {
    ...page,
    content,
  }
}
